import { css } from '@emotion/core'
import { Bleed, Button, Checkbox, colors, QuickGlanceOverlay, Row, TextField } from '@fs/zion-ui'
import React, { useCallback, useState, useEffect } from 'react'
import transl from '../imageQualityAudit.transl'
import { useImageAuditContext } from '../../../hooks/useImageAuditContext'

const qualityIssues = [
  'defective',
  'duplicate',
  'extraMaterialInFrame',
  'improperlyCropped',
  'informationObscured',
  'missingPages',
  'motionBlurOrOutOfFocus',
  'multiple',
  'overUnderExposed',
  'reflections',
  'skewed',
  'wrongCaptureMode',
  'wrongImageOrientation',
]

const optionsList = css`
  max-height: 300px;
  overflow-y: scroll;
  padding-inline-start: 8px;
`

const footerStyles = css`
  padding-top: 12px;
  border-top: 1px solid ${colors.gray10};
  padding: 16px;
  width: 400px;
`

const inputWrapper = css`
  padding-inline-start: 28px;
  padding-inline-end: 16px;
`

export default function MultiSelectWindow({ currentItem, overlay }) {
  const itemIssues = currentItem?.auditIssues
  const [otherReason, setOtherReason] = useState()
  const [currentSelection, setCurrentSelection] = useState(itemIssues || [])
  const [allowOtherComment, setAllowOtherComment] = useState(false)
  const { updateImageIssues, focusedIndex } = useImageAuditContext()

  const saveSelection = useCallback(() => {
    let newSelection = currentSelection
    if (allowOtherComment) {
      const otherItemCheck = newSelection.find((i) => !qualityIssues.includes(i))
      if (!otherItemCheck) newSelection.push(otherReason)
      else {
        const idx = newSelection.indexOf(otherItemCheck)
        newSelection[idx] = otherReason
      }
    } else newSelection = newSelection.filter((i) => qualityIssues.includes(i))

    const mappedIssues = newSelection.map((i) => {
      if (transl[i]) return { key: i, primaryText: transl[i]() }
      return { key: 'other', primaryText: i }
    })

    updateImageIssues(mappedIssues)
    overlay.handleClick()
  }, [currentSelection, allowOtherComment, updateImageIssues, overlay, otherReason])

  useEffect(() => {
    if (currentItem && overlay.isOpen) {
      const otherItem = itemIssues.find((i) => i?.key === 'other')
      if (otherItem) {
        setAllowOtherComment(true)
        setOtherReason(otherItem.primaryText)
      } else {
        setOtherReason(null)
        setAllowOtherComment(false)
      }

      const filteredIssues = itemIssues.filter((i) => i !== 'meetsQualityStandards')
      const strippedIssues = filteredIssues.map((i) => i.key)
      setCurrentSelection(strippedIssues)
    }
  }, [currentItem, itemIssues, overlay])

  const onChange = useCallback(
    ({ target }) => {
      const { value } = target
      const inArray = currentSelection.includes(value)
      if (inArray) setCurrentSelection((val) => val.filter((item) => item !== value))
      else setCurrentSelection((current) => [...current, value])
    },
    [currentSelection]
  )

  const options = qualityIssues?.map((mi) => ({ label: transl?.[mi](), value: mi }))

  const cancelMenu = useCallback(() => {
    setCurrentSelection(itemIssues)
    setOtherReason(null)
    overlay.handleClick()
  }, [itemIssues, overlay])

  const disableSave = allowOtherComment && !otherReason

  return (
    <QuickGlanceOverlay
      {...overlay}
      title={transl.addIssueToItem(focusedIndex + 1)}
      placement="top"
      nonClosable
      maxWidth="400px"
      footer={
        <Bleed all>
          <div css={footerStyles}>
            <Row alignX="end">
              <Button onClick={cancelMenu}>{transl.cancel()}</Button>
              <Button onClick={saveSelection} disabled={disableSave}>
                {transl.save()}
              </Button>
            </Row>
          </div>
        </Bleed>
      }
    >
      <div css={optionsList}>
        {options?.map((option) => (
          <Checkbox
            label={option.label}
            checked={currentSelection?.includes(option.value)}
            onChange={onChange}
            key={option.value}
            value={option.value}
          />
        ))}
        <Checkbox
          checked={allowOtherComment}
          onChange={() => setAllowOtherComment((val) => !val)}
          label={transl.other()}
        />
        {allowOtherComment && (
          <div css={inputWrapper}>
            <TextField
              value={otherReason}
              onChange={({ target }) => setOtherReason(target.value)}
              aria-label={transl.other()}
              data-testid="other-reason-input"
            />
          </div>
        )}
      </div>
    </QuickGlanceOverlay>
  )
}
